import React, { useEffect, useState, useRef, Children, memo } from 'react';
import { MessageList } from '../components/MessageList';
import { BottomBar, FamilyMember } from '../components/BottomBar';
import { Message } from '../components/MessageBubble';
import { ChecklistItem } from '../components/ChecklistCard';
import { TaskModal } from '../components/TaskModal';
import { OverwriteConfirmationModal } from '../components/OverwriteConfirmationModal';
import { EmptyChatSuggestions, YeppyChatMode } from '../components/EmptyChatSuggestions';
import { ConversationEngine } from '../services/conversationEngine';
import { mockConversations } from '../services/mockConversations';
// Interface for global checklist state
interface GlobalChecklist {
  title: string;
  items: ChecklistItem[];
  categoryColor?: string;
  originalMessageId?: string;
}
// Default children data
const defaultChildren: FamilyMember[] = [{
  id: 'child-1',
  name: 'Sofia',
  avatar: 'https://cdn.magicpatterns.com/uploads/c3jxasi4fCCZ93c9aemubP/adele-shafiee-KCvjle4_4ps-unsplash.jpg',
  type: 'child'
}, {
  id: 'child-2',
  name: 'Marco',
  avatar: 'https://cdn.magicpatterns.com/uploads/1EXQ4FkeqG6UuqJKWikck9/tatev-arsh-ZtB5Haz58os-unsplash.jpg',
  type: 'child'
}];
interface ChatPageProps {
  isMenuOpen?: boolean;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  conversationId?: string | null;
  onNewConversation?: () => void;
  onBackToHistory?: () => void;
  onChatTitleChange?: (title: string) => void;
  onOpenConversation?: (conversationId: string) => void;
  chatMode?: YeppyChatMode;
  onModeChange?: (mode: YeppyChatMode) => void;
  onHasMessagesChange?: (hasMessages: boolean) => void;
  initialMessage?: string | null;
  onInitialMessageConsumed?: () => void;
  onOpenChild?: (childId: string) => void;
}
export function ChatPage({
  isMenuOpen,
  onMenuOpen,
  onMenuClose,
  conversationId,
  onNewConversation,
  onBackToHistory,
  onChatTitleChange,
  onOpenConversation,
  chatMode,
  onModeChange,
  onHasMessagesChange,
  initialMessage,
  onInitialMessageConsumed,
  onOpenChild
}: ChatPageProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [chatTitle, setChatTitle] = useState('Nuova chat');
  // Use props if provided, otherwise local state
  const [localChatMode, setLocalChatMode] = useState<YeppyChatMode>('attivita');
  const currentChatMode = chatMode || localChatMode;
  const handleModeChange = onModeChange || setLocalChatMode;
  const [participants, setParticipants] = useState<FamilyMember[]>([]);
  const [selectedChild, setSelectedChild] = useState<FamilyMember>(defaultChildren[0]);
  // Conversation engine instance
  const conversationEngineRef = useRef(new ConversationEngine());
  // Global state for all checklists
  const [globalChecklists, setGlobalChecklists] = useState<GlobalChecklist[]>([]);
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedChecklist, setSelectedChecklist] = useState<GlobalChecklist | null>(null);
  // Saved info banner state
  const [savedInfoBanner, setSavedInfoBanner] = useState<{
    childName: string;
    childId: string;
    childAvatar: string;
    category: string;
  } | null>(null);
  const savedInfoTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Overwrite confirmation state
  const [isOverwriteModalOpen, setIsOverwriteModalOpen] = useState(false);
  const [pendingChecklistUpdate, setPendingChecklistUpdate] = useState<{
    newChecklist: GlobalChecklist;
    existingIndex: number;
  } | null>(null);
  // Ref to track if we should scroll to bottom
  const shouldScrollRef = useRef(false);
  // Load conversation from history
  useEffect(() => {
    if (conversationId && mockConversations[conversationId]) {
      const conversation = mockConversations[conversationId];
      // Set chat title from conversation
      setChatTitle(conversation.title || 'Chat');
      if (onChatTitleChange) {
        onChatTitleChange(conversation.title || 'Chat');
      }
      // Convert mock messages to Message format
      const loadedMessages: Message[] = conversation.messages.map(msg => ({
        id: msg.id,
        type: msg.type,
        content: msg.content,
        timestamp: msg.timestamp,
        categoryColor: msg.tasks?.[0]?.categoryColor,
        checklist: msg.tasks?.[0] ? {
          title: msg.tasks[0].title,
          items: msg.tasks[0].items
        } : undefined
      }));
      // Extract checklists from messages
      const checklists: GlobalChecklist[] = [];
      conversation.messages.forEach(msg => {
        if (msg.tasks) {
          msg.tasks.forEach(task => {
            checklists.push({
              title: task.title,
              items: task.items,
              categoryColor: task.categoryColor,
              originalMessageId: msg.id
            });
          });
        }
      });
      setMessages(loadedMessages);
      setGlobalChecklists(checklists);
      // Set flag to scroll to bottom after messages are rendered
      shouldScrollRef.current = true;
    } else if (!conversationId) {
      // Reset for new chat
      setMessages([]);
      setGlobalChecklists([]);
      setChatTitle('Nuova chat');
      if (onChatTitleChange) {
        onChatTitleChange('Nuova chat');
      }
      setParticipants([]);
      setIsTyping(false);
      conversationEngineRef.current.reset();
    }
  }, [conversationId, onChatTitleChange]);
  // Mode-specific shortcuts for initial messages
  const modeShortcuts: Record<string, {
    label: string;
    prompt: string;
  }[]> = {
    ascolto: [{
      label: '🌙 Sofia ha paura del buio',
      prompt: 'Sofia ha paura del buio, usa una lucina per dormire'
    }, {
      label: '🍝 Marco oggi ha mangiato poco',
      prompt: 'Marco oggi ha mangiato poco a pranzo, non voleva la pasta'
    }, {
      label: '💃 Sofia ha iniziato danza',
      prompt: 'Sofia ha iniziato il corso di danza il mercoledì pomeriggio'
    }, {
      label: '👟 Marco porta il 28 di scarpe',
      prompt: 'Marco adesso porta il 28 di scarpe, è cresciuto'
    }],
    informazioni: [{
      label: '👟 Taglia scarpe di Sofia?',
      prompt: 'Che taglia di scarpe porta Sofia?'
    }, {
      label: '👨‍⚕️ Pediatra di Marco?',
      prompt: 'Chi è il pediatra di Marco?'
    }, {
      label: '😴 A che ora va a nanna Sofia?',
      prompt: 'A che ora va a nanna Sofia?'
    }, {
      label: '🏫 Che scuola frequenta Marco?',
      prompt: 'Che scuola frequenta Marco?'
    }],
    attivita: [{
      label: '🏫 Iscrivere Sofia a scuola',
      prompt: 'Devo iscrivere Sofia alla scuola elementare'
    }, {
      label: '👨‍⚕️ Visita dal pediatra',
      prompt: 'Devo prenotare la visita dal pediatra per Marco'
    }, {
      label: '🧳 Preparare valigia vacanze',
      prompt: 'Devo preparare la valigia per le vacanze dei bambini'
    }, {
      label: '📋 Documenti per il campo estivo',
      prompt: 'Devo preparare i documenti per il campo estivo di Sofia'
    }]
  };
  // Inject initial message from Home shortcuts
  useEffect(() => {
    if (initialMessage && messages.length === 0 && !conversationId) {
      // In ascolto mode, skip shortcuts so the user can start typing right away
      const shortcuts = currentChatMode === 'ascolto' ? undefined : modeShortcuts[currentChatMode] || [];
      const assistantMessage: Message = {
        id: `assistant-init-${Date.now()}`,
        type: 'assistant',
        content: initialMessage,
        timestamp: new Date(),
        shortcuts
      };
      setMessages([assistantMessage]);
      onInitialMessageConsumed?.();
    }
  }, [initialMessage, conversationId]);
  // Scroll to bottom when messages change and shouldScroll is true
  useEffect(() => {
    if (shouldScrollRef.current && messages.length > 0) {
      setTimeout(() => {
        const messageList = document.querySelector('[data-message-list]');
        if (messageList) {
          messageList.scrollTop = messageList.scrollHeight;
        }
        shouldScrollRef.current = false;
      }, 100);
    }
  }, [messages]);
  // Update chat title based on first message
  useEffect(() => {
    if (messages.length > 0 && chatTitle === 'Nuova chat') {
      const firstUserMessage = messages.find(m => m.type === 'user');
      if (firstUserMessage) {
        // Truncate to first 30 chars
        const title = firstUserMessage.content.slice(0, 30) + (firstUserMessage.content.length > 30 ? '...' : '');
        setChatTitle(title);
        if (onChatTitleChange) {
          onChatTitleChange(title);
        }
      }
    }
  }, [messages, chatTitle, onChatTitleChange]);
  // Notify parent when messages change
  useEffect(() => {
    onHasMessagesChange?.(messages.length > 0);
  }, [messages.length, onHasMessagesChange]);
  // Helper to detect which child is mentioned and show saved info banner
  const detectChildAndShowBanner = (userInput: string) => {
    const input = userInput.toLowerCase();
    const children = [{
      name: 'Sofia',
      id: '1',
      avatar: 'https://cdn.magicpatterns.com/uploads/c3jxasi4fCCZ93c9aemubP/adele-shafiee-KCvjle4_4ps-unsplash.jpg'
    }, {
      name: 'Marco',
      id: '2',
      avatar: 'https://cdn.magicpatterns.com/uploads/1EXQ4FkeqG6UuqJKWikck9/tatev-arsh-ZtB5Haz58os-unsplash.jpg'
    }];
    let detectedChild = children[0]; // default to Sofia
    if (input.includes('marco')) detectedChild = children[1];
    if (input.includes('sofia')) detectedChild = children[0];
    // Detect category
    let category = 'Info generali';
    if (input.includes('scuola') || input.includes('maestra') || input.includes('classe')) category = 'Scuola';
    if (input.includes('paura') || input.includes('dorme') || input.includes('nanna') || input.includes('buio')) category = 'Abitudini';
    if (input.includes('mangia') || input.includes('cibo') || input.includes('pranzo') || input.includes('cena') || input.includes('broccoli') || input.includes('pizza') || input.includes('pesto')) category = 'Alimentazione';
    if (input.includes('scarpe') || input.includes('taglia') || input.includes('vestiti')) category = 'Taglie';
    if (input.includes('danza') || input.includes('nuoto') || input.includes('sport') || input.includes('corso')) category = 'Attività';
    if (input.includes('pediatra') || input.includes('medico') || input.includes('vaccino') || input.includes('peso')) category = 'Salute';
    if (input.includes('peluche') || input.includes('giocattol') || input.includes('lego')) category = 'Preferenze';
    // Clear previous timer
    if (savedInfoTimerRef.current) clearTimeout(savedInfoTimerRef.current);
    setSavedInfoBanner({
      childName: detectedChild.name,
      childId: detectedChild.id,
      childAvatar: detectedChild.avatar,
      category
    });
    // Auto-dismiss after 6 seconds
    savedInfoTimerRef.current = setTimeout(() => {
      setSavedInfoBanner(null);
    }, 6000);
  };
  // Generate a simulated response based on chat mode when engine doesn't match
  const generateFallbackResponse = (userInput: string, mode: YeppyChatMode): {
    message: string;
    checklist?: {
      title: string;
      items: ChecklistItem[];
    };
    shortcuts?: {
      label: string;
      prompt: string;
    }[];
  } => {
    const input = userInput.toLowerCase();
    if (mode === 'ascolto') {
      // Ascolto mode: acknowledge info, confirm saved, ask follow-up
      if (input.includes('paura') || input.includes('buio')) {
        return {
          message: "Ho capito, ho salvato che ha paura del buio e usa una lucina per dormire 🌙 È una cosa molto comune alla sua età! Vuoi aggiungere altri dettagli o raccontarmi qualcos'altro?",
          shortcuts: [{
            label: '😴 Come dorme di solito?',
            prompt: 'Di solito dorme bene, si sveglia verso le 7'
          }, {
            label: '🧸 Ha un peluche preferito?',
            prompt: 'Ha un peluche a forma di coniglio che porta sempre a letto'
          }]
        };
      }
      if (input.includes('mangiato') || input.includes('mangia') || input.includes('pranzo') || input.includes('cena')) {
        return {
          message: "Annotato! Ho registrato le informazioni sull'alimentazione 🍽️ Capita a tutti i bimbi di avere giornate così. Vuoi dirmi di più?",
          shortcuts: [{
            label: '🥦 Cibi che non ama',
            prompt: 'Non gli piacciono le verdure, soprattutto i broccoli'
          }, {
            label: '🍕 Cibi preferiti',
            prompt: 'Adora la pizza e la pasta al pesto'
          }]
        };
      }
      if (input.includes('danza') || input.includes('nuoto') || input.includes('sport') || input.includes('corso')) {
        return {
          message: "Perfetto, ho salvato l'attività! 💃 Aggiornerò il calendario con gli orari. Vuoi aggiungere altri dettagli?",
          shortcuts: [{
            label: '📅 Che giorni?',
            prompt: 'Il corso è il mercoledì dalle 17 alle 18'
          }, {
            label: '👕 Serve qualcosa?',
            prompt: 'Deve portare body rosa e scarpette da danza'
          }]
        };
      }
      if (input.includes('scarpe') || input.includes('taglia') || input.includes('vestiti') || input.includes('cresciuto')) {
        return {
          message: 'Aggiornato! Ho salvato la nuova taglia 👟 Terrò traccia dei cambiamenti. Vuoi aggiornare altre taglie?',
          shortcuts: [{
            label: '👕 Taglia vestiti',
            prompt: 'Adesso veste la 6-7 anni, circa 122cm'
          }, {
            label: '🧢 Taglia cappello',
            prompt: 'Il cappello è taglia 52'
          }]
        };
      }
      if (input.includes('scuola') || input.includes('maestra') || input.includes('classe')) {
        return {
          message: 'Salvato! Ho aggiornato le informazioni sulla scuola 🏫 Vuoi aggiungere altri dettagli?',
          shortcuts: [{
            label: '👩‍🏫 Nome maestra',
            prompt: 'La maestra si chiama Laura Verdi'
          }, {
            label: '⏰ Orari',
            prompt: "L'orario è dalle 8:30 alle 16:00"
          }]
        };
      }
      if (input.includes('pediatra') || input.includes('medico') || input.includes('dottore') || input.includes('vaccino')) {
        return {
          message: 'Ho registrato le informazioni sulla salute 🏥 È importante tenere tutto aggiornato. Altro da aggiungere?',
          shortcuts: [{
            label: '💉 Prossimo vaccino',
            prompt: 'Il prossimo richiamo è previsto per marzo 2025'
          }, {
            label: '⚖️ Peso attuale',
            prompt: 'Pesa circa 22 kg'
          }]
        };
      }
      if (input.includes('dorme') || input.includes('sveglia') || input.includes('nanna') || input.includes('notte')) {
        return {
          message: 'Perfetto, ho aggiornato le abitudini del sonno 😴 Informazione importante da tenere a mente!',
          shortcuts: [{
            label: '🛏️ Routine della sera',
            prompt: 'La sera leggiamo sempre una storia prima di dormire'
          }, {
            label: '⏰ A che ora si sveglia',
            prompt: 'Di solito si sveglia alle 7 da solo'
          }]
        };
      }
      if (input.includes('peluche') || input.includes('giocattol') || input.includes('pupazz') || input.includes('coniglio')) {
        return {
          message: 'Che carino! Ho salvato questa informazione 🧸 I dettagli sugli oggetti del cuore sono importantissimi!',
          shortcuts: [{
            label: '🎮 Giochi preferiti',
            prompt: 'Adora giocare con i Lego e colorare'
          }, {
            label: '📚 Libri preferiti',
            prompt: 'Il suo libro preferito è Il Gruffalò'
          }]
        };
      }
      if (input.includes('tutto') || input.includes('basta') || input.includes('grazie') || input.includes('finito')) {
        return {
          message: 'Perfetto, ho salvato tutto! 🎉 Quando vuoi aggiungere altre informazioni, sono qui. Buona giornata!'
        };
      }
      if (input.includes('broccoli') || input.includes('verdur') || input.includes('pizza') || input.includes('pesto') || input.includes('piace') || input.includes('preferit')) {
        return {
          message: 'Annotato nei gusti alimentari! 🍽️ Queste info sono utilissime per babysitter e nonni.',
          shortcuts: [{
            label: '🥤 Bevande',
            prompt: 'Beve solo acqua e succo di mela, niente bibite'
          }, {
            label: '🍰 Dolci',
            prompt: 'Come dolce preferisce la torta al cioccolato'
          }]
        };
      }
      // Generic ascolto fallback — extract child name if possible
      const childNames = ['sofia', 'marco', 'edoardo', 'edo'];
      const mentionedChild = childNames.find(name => input.includes(name));
      const childDisplay = mentionedChild ? mentionedChild === 'edo' ? 'Edoardo' : mentionedChild.charAt(0).toUpperCase() + mentionedChild.slice(1) : null;
      return {
        message: childDisplay ? `Capito, ho registrato questa informazione su ${childDisplay}! ✅ Vuoi raccontarmi qualcos'altro?` : `Capito, ho registrato questa informazione! ✅ Vuoi raccontarmi qualcos'altro sui bimbi?`,
        shortcuts: [{
          label: '📝 Racconta altro',
          prompt: 'Voglio aggiungere altre informazioni'
        }, {
          label: '✅ Per ora basta',
          prompt: 'Per ora è tutto, grazie!'
        }]
      };
    }
    if (mode === 'informazioni') {
      // Helper to detect which child is mentioned
      const mentionsSofia = input.includes('sofia');
      const mentionsMarco = input.includes('marco');
      const mentionsEdoardo = input.includes('edo') || input.includes('edoardo');
      // Scarpe / taglie
      if (input.includes('scarpe') || input.includes('taglia')) {
        if (mentionsSofia) return {
          message: "Sofia porta il numero 32 di scarpe 👟 L'ultimo aggiornamento è di questo mese."
        };
        if (mentionsMarco) return {
          message: 'Marco porta il numero 28 di scarpe 👟 Sta crescendo in fretta!'
        };
        if (mentionsEdoardo) return {
          message: 'Edoardo porta il numero 24 di scarpe 👟 Aggiornato la settimana scorsa.'
        };
        return {
          message: 'Sofia porta il 32, Marco il 28 e Edoardo il 24 👟'
        };
      }
      // Pediatra / medico
      if (input.includes('pediatra') || input.includes('medico') || input.includes('dottore')) {
        return {
          message: 'Il pediatra è la Dott.ssa Bianchi 👩‍⚕️ Studio in Via Roma 15, tel. 011 123 4567. Riceve lunedì e mercoledì 14-18.'
        };
      }
      // Nanna / dormire
      if (input.includes('nanna') || input.includes('dormire') || input.includes('letto') || input.includes('dorme')) {
        if (mentionsSofia) return {
          message: 'Sofia va a nanna alle 20:30 🌙 Ha bisogno della lucina perché ha paura del buio.'
        };
        if (mentionsMarco) return {
          message: 'Marco va a nanna alle 20:00 🌙 Ha bisogno del doudou blu per addormentarsi.'
        };
        if (mentionsEdoardo) return {
          message: 'Edoardo va a nanna alle 19:30 🌙 Si addormenta con la ninna nanna.'
        };
        return {
          message: 'Sofia va a nanna alle 20:30, Marco alle 20:00 e Edoardo alle 19:30 🌙'
        };
      }
      // Scuola
      if (input.includes('scuola') || input.includes('classe') || input.includes('maestra') || input.includes('frequenta')) {
        if (mentionsSofia) return {
          message: "Sofia frequenta l'Elementare G. Rodari 🏫 Classe 1^ B, maestra Laura Verdi. Orario: 8:30 – 16:00."
        };
        if (mentionsMarco) return {
          message: 'Marco frequenta la Materna Arcobaleno 🏫 Sezione Gialli, con la Maestra Anna. Orario: 8:00 – 16:00.'
        };
        if (mentionsEdoardo) return {
          message: 'Edoardo frequenta il Nido Piccoli Passi 🏫 Sezione Coccinelle. Orario: 7:30 – 15:30.'
        };
        return {
          message: 'Sofia → Elementare G. Rodari (1^B), Marco → Materna Arcobaleno (Gialli), Edoardo → Nido Piccoli Passi 🏫'
        };
      }
      // Allergie
      if (input.includes('allergi') || input.includes('intolleranz')) {
        return {
          message: "Sofia non ha allergie note ✅ Marco ha un'intolleranza lieve al lattosio 🥛 Edoardo: nessuna allergia nota."
        };
      }
      // Documenti
      if (input.includes('codice fiscale') || input.includes('documento') || input.includes('tessera')) {
        return {
          message: 'Sofia: CF RSSSFO18C55H501X · Tessera sanitaria 803800005003038589\nMarco: CF RSSMRC20L22H501Y · Tessera sanitaria 803800005003038590 📄'
        };
      }
      // Compleanno / età
      if (input.includes('compleanno') || input.includes('anni') || input.includes('nato') || input.includes('età')) {
        if (mentionsSofia) return {
          message: 'Sofia ha 6 anni, nata il 15 marzo 2019 🎂'
        };
        if (mentionsMarco) return {
          message: 'Marco ha 4 anni, nato il 22 luglio 2021 🎂'
        };
        if (mentionsEdoardo) return {
          message: 'Edoardo ha 2 anni, nato il 5 gennaio 2023 🎂'
        };
        return {
          message: 'Sofia: 6 anni (15/03/2019), Marco: 4 anni (22/07/2021), Edoardo: 2 anni (05/01/2023) 🎂'
        };
      }
      // Cibo / mangiare
      if (input.includes('mangia') || input.includes('cibo') || input.includes('dieta')) {
        if (mentionsSofia) return {
          message: 'Sofia mangia di tutto tranne i funghi 🍽️ Adora la pasta al pesto e la pizza margherita.'
        };
        if (mentionsMarco) return {
          message: "Marco è un po' selettivo: ama la pasta in bianco e i bastoncini di pesce 🍽️ Intollerante al lattosio."
        };
        return {
          message: 'Sofia mangia quasi tutto, Marco è selettivo (intollerante al lattosio), Edoardo sta iniziando lo svezzamento 🍽️'
        };
      }
      // Attività / sport / hobby
      if (input.includes('sport') || input.includes('attività') || input.includes('hobby') || input.includes('corso') || input.includes('danza') || input.includes('nuoto')) {
        if (mentionsSofia) return {
          message: 'Sofia fa danza il mercoledì 17-18 💃 e nuoto il sabato mattina 10-11 🏊'
        };
        if (mentionsMarco) return {
          message: 'Marco fa mini-basket il giovedì 16:30-17:30 🏀'
        };
        return {
          message: 'Sofia: danza (mer 17-18) + nuoto (sab 10-11). Marco: mini-basket (gio 16:30-17:30) 🏃'
        };
      }
      // Generic informazioni fallback
      return {
        message: `Fammi cercare... 🔍 Non ho trovato informazioni specifiche su questo argomento. Prova a chiedermi qualcosa di più preciso!`,
        shortcuts: [{
          label: '👟 Taglie',
          prompt: 'Che taglia di scarpe portano i bambini?'
        }, {
          label: '🏫 Scuola',
          prompt: 'Che scuola frequentano i bambini?'
        }, {
          label: '👨‍⚕️ Pediatra',
          prompt: 'Chi è il pediatra?'
        }, {
          label: '🎂 Compleanni',
          prompt: 'Quando sono nati i bambini?'
        }]
      };
    }
    if (mode === 'attivita') {
      // Attivita mode: try engine first, then create a simple checklist
      if (input.includes('iscrivere') || input.includes('iscrizione')) {
        return {
          message: "Ecco la lista per l'iscrizione! Ho preparato tutti i passaggi 📋",
          checklist: {
            title: 'Iscrizione scuola',
            items: [{
              id: 'i1',
              text: 'Raccogliere documenti (C.I., codice fiscale, certificato vaccinazioni)',
              completed: false
            }, {
              id: 'i2',
              text: 'Compilare modulo iscrizione online',
              completed: false
            }, {
              id: 'i3',
              text: 'Scegliere tempo pieno o modulare',
              completed: false
            }, {
              id: 'i4',
              text: 'Consegnare documenti in segreteria',
              completed: false
            }, {
              id: 'i5',
              text: 'Confermare iscrizione mensa',
              completed: false
            }]
          }
        };
      }
      if (input.includes('pediatra') || input.includes('visita') || input.includes('medico')) {
        return {
          message: 'Ho creato la lista per la visita dal pediatra! 🏥',
          checklist: {
            title: 'Visita pediatra',
            items: [{
              id: 'v1',
              text: 'Chiamare studio per appuntamento (011 123 4567)',
              completed: false
            }, {
              id: 'v2',
              text: 'Preparare libretto sanitario',
              completed: false
            }, {
              id: 'v3',
              text: 'Segnare domande da fare al dottore',
              completed: false
            }, {
              id: 'v4',
              text: 'Controllare data ultimo vaccino',
              completed: false
            }]
          }
        };
      }
      if (input.includes('valigia') || input.includes('vacanz')) {
        return {
          message: 'Ecco la lista per preparare la valigia! 🧳',
          checklist: {
            title: 'Valigia vacanze bambini',
            items: [{
              id: 'va1',
              text: 'Vestiti (5 cambi + pigiami)',
              completed: false
            }, {
              id: 'va2',
              text: 'Costume e ciabatte piscina/mare',
              completed: false
            }, {
              id: 'va3',
              text: 'Crema solare e cappellino',
              completed: false
            }, {
              id: 'va4',
              text: 'Medicinali e cerotti',
              completed: false
            }, {
              id: 'va5',
              text: 'Giochi e libri per il viaggio',
              completed: false
            }, {
              id: 'va6',
              text: 'Doudou di Marco e lucina di Sofia',
              completed: false
            }]
          }
        };
      }
      if (input.includes('campo estivo') || input.includes('documenti')) {
        return {
          message: 'Ecco i documenti da preparare per il campo estivo! 📋',
          checklist: {
            title: 'Documenti campo estivo',
            items: [{
              id: 'ce1',
              text: 'Modulo iscrizione compilato',
              completed: false
            }, {
              id: 'ce2',
              text: 'Certificato medico sportivo',
              completed: false
            }, {
              id: 'ce3',
              text: 'Fotocopia tessera sanitaria',
              completed: false
            }, {
              id: 'ce4',
              text: 'Liberatoria firmata da entrambi i genitori',
              completed: false
            }, {
              id: 'ce5',
              text: 'Segnalare allergie/intolleranze',
              completed: false
            }]
          }
        };
      }
      // Generic attivita fallback
      return {
        message: `Ok! Dimmi di più su cosa devi organizzare e creerò una lista di attività per te ✅`,
        shortcuts: [{
          label: '🏫 Scuola',
          prompt: 'Devo iscrivere Sofia alla scuola elementare'
        }, {
          label: '🏥 Salute',
          prompt: 'Devo prenotare la visita dal pediatra per Marco'
        }, {
          label: '🧳 Vacanze',
          prompt: 'Devo preparare la valigia per le vacanze dei bambini'
        }]
      };
    }
    return {
      message: `Capito! Come posso aiutarti? 😊`
    };
  };
  const handleSendMessage = async (content: string) => {
    // Add user message
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      type: 'user',
      content,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);
    // Simulate brief delay for natural feel
    setTimeout(() => {
      let assistantMessage: Message;
      // In ascolto/informazioni modes, ALWAYS use smart fallback (no engine)
      // In attivita mode, try engine first, then fallback
      if (currentChatMode === 'ascolto' || currentChatMode === 'informazioni') {
        const fallback = generateFallbackResponse(content, currentChatMode);
        assistantMessage = {
          id: `assistant-${Date.now()}`,
          type: 'assistant',
          content: fallback.message,
          categoryColor: '#B0EC9C',
          timestamp: new Date(),
          checklist: fallback.checklist,
          shortcuts: fallback.shortcuts
        };
        // Show saved info banner in ascolto mode
        if (currentChatMode === 'ascolto') {
          detectChildAndShowBanner(content);
        }
      } else {
        // Attivita mode: try engine first, then fallback
        const response = conversationEngineRef.current.startConversation(content);
        const engineMatched = !response.message.includes('Mi dispiace, non ho capito');
        if (engineMatched) {
          assistantMessage = {
            id: `assistant-${Date.now()}`,
            type: 'assistant',
            content: response.message,
            categoryColor: '#B0EC9C',
            timestamp: new Date(),
            widget: response.widget,
            checklist: response.checklist
          };
        } else {
          const fallback = generateFallbackResponse(content, currentChatMode);
          assistantMessage = {
            id: `assistant-${Date.now()}`,
            type: 'assistant',
            content: fallback.message,
            categoryColor: '#B0EC9C',
            timestamp: new Date(),
            checklist: fallback.checklist,
            shortcuts: fallback.shortcuts
          };
        }
      }
      // Handle checklist if present
      if (assistantMessage.checklist) {
        const newChecklist = {
          ...assistantMessage.checklist,
          categoryColor: '#B0EC9C',
          originalMessageId: assistantMessage.id
        };
        // Check if this is an update to an existing checklist
        const existingIndex = globalChecklists.findIndex(c => c.title === newChecklist.title);
        if (existingIndex >= 0) {
          const existingList = globalChecklists[existingIndex];
          const hasCompletedItems = existingList.items.some(i => i.completed);
          if (hasCompletedItems) {
            setPendingChecklistUpdate({
              newChecklist,
              existingIndex
            });
            setIsOverwriteModalOpen(true);
          } else {
            const updatedLists = [...globalChecklists];
            updatedLists[existingIndex] = newChecklist;
            setGlobalChecklists(updatedLists);
          }
        } else {
          setGlobalChecklists(prev => [...prev, newChecklist]);
        }
      }
      setMessages(prev => [...prev, assistantMessage]);
      setIsTyping(false);
    }, 500);
  };
  const handleWidgetResponse = (response: string) => {
    setIsTyping(true);
    setTimeout(() => {
      const engineResponse = conversationEngineRef.current.processResponse(response);
      const assistantMessage: Message = {
        id: `assistant-${Date.now()}`,
        type: 'assistant',
        content: engineResponse.message,
        timestamp: new Date(),
        widget: engineResponse.widget,
        checklist: engineResponse.checklist
      };
      if (engineResponse.checklist) {
        const newChecklist = {
          ...engineResponse.checklist,
          categoryColor: '#B0EC9C',
          originalMessageId: assistantMessage.id
        };
        assistantMessage.categoryColor = '#B0EC9C';
        setGlobalChecklists(prev => [...prev, newChecklist]);
      }
      setMessages(prev => [...prev, assistantMessage]);
      setIsTyping(false);
    }, 500);
  };
  const handleWidgetSkip = () => {
    setIsTyping(true);
    setTimeout(() => {
      const engineResponse = conversationEngineRef.current.skipStep();
      const assistantMessage: Message = {
        id: `assistant-${Date.now()}`,
        type: 'assistant',
        content: engineResponse.message,
        timestamp: new Date(),
        widget: engineResponse.widget,
        checklist: engineResponse.checklist
      };
      if (engineResponse.checklist) {
        const newChecklist = {
          ...engineResponse.checklist,
          categoryColor: '#B0EC9C',
          originalMessageId: assistantMessage.id
        };
        assistantMessage.categoryColor = '#B0EC9C';
        setGlobalChecklists(prev => [...prev, newChecklist]);
      }
      setMessages(prev => [...prev, assistantMessage]);
      setIsTyping(false);
    }, 500);
  };
  const handleOverwrite = () => {
    if (pendingChecklistUpdate) {
      const {
        newChecklist,
        existingIndex
      } = pendingChecklistUpdate;
      const updatedLists = [...globalChecklists];
      updatedLists[existingIndex] = newChecklist;
      setGlobalChecklists(updatedLists);
    }
    setIsOverwriteModalOpen(false);
    setPendingChecklistUpdate(null);
  };
  const handleKeepCompleted = () => {
    if (pendingChecklistUpdate) {
      const {
        newChecklist,
        existingIndex
      } = pendingChecklistUpdate;
      const existingList = globalChecklists[existingIndex];
      const mergedItems = newChecklist.items.map(newItem => {
        const existingItem = existingList.items.find(old => old.id === newItem.id || old.text === newItem.text);
        return existingItem && existingItem.completed ? {
          ...newItem,
          completed: true
        } : newItem;
      });
      const mergedChecklist = {
        ...newChecklist,
        items: mergedItems
      };
      const updatedLists = [...globalChecklists];
      updatedLists[existingIndex] = mergedChecklist;
      setGlobalChecklists(updatedLists);
    }
    setIsOverwriteModalOpen(false);
    setPendingChecklistUpdate(null);
  };
  const handlePreviewClick = (checklist: {
    title: string;
    items: ChecklistItem[];
  }) => {
    const liveChecklist = globalChecklists.find(c => c.title === checklist.title) || {
      ...checklist,
      categoryColor: '#B0EC9C'
    };
    setSelectedChecklist(liveChecklist);
    setIsModalOpen(true);
  };
  const handleAddParticipant = (member: FamilyMember) => {
    setParticipants(prev => [...prev, member]);
  };
  const handleRemoveParticipant = (memberId: string) => {
    setParticipants(prev => prev.filter(p => p.id !== memberId));
  };
  const handleChildChange = (child: FamilyMember) => {
    setSelectedChild(child);
  };
  // Handle shortcut click - sends the prompt as a message
  const handleShortcutClick = (prompt: string) => {
    handleSendMessage(prompt);
  };
  // Handle template button click
  const handleUseTemplate = () => {
    // TODO: Open template selection modal
    console.log('Open template selection');
  };
  return <div className="flex flex-col w-full h-full relative" data-id="element-648">
      {/* Saved info banner */}
      {savedInfoBanner && <div className="mx-3 mt-2 mb-0" style={{
      animation: 'bannerSlideIn 0.35s ease-out'
    }} data-id="element-649">
          <button onClick={() => {
        onOpenChild?.(savedInfoBanner.childId);
        setSavedInfoBanner(null);
      }} className="w-full flex items-center gap-3 px-3.5 py-2.5 bg-yeppy-green/15 border border-yeppy-green/30 rounded-2xl transition-all active:scale-[0.98]" data-id="element-650">
            <img src={savedInfoBanner.childAvatar} alt={savedInfoBanner.childName} className="w-8 h-8 rounded-full object-cover flex-shrink-0" data-id="element-651" />
            <div className="flex-1 min-w-0 text-left" data-id="element-652">
              <p className="font-poppins text-xs font-medium text-yeppy-text leading-tight" data-id="element-653">
                ✅ Salvato nella scheda di{' '}
                <span className="font-semibold" data-id="element-654">
                  {savedInfoBanner.childName}
                </span>
              </p>
              <p className="font-poppins text-[11px] text-yeppy-text/50 leading-tight" data-id="element-655">
                {savedInfoBanner.category} · Tocca per vedere
              </p>
            </div>
            <svg className="w-4 h-4 text-yeppy-text/40 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-656">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" data-id="element-657" />
            </svg>
          </button>
        </div>}

      {/* Chat content */}
      <div className="flex flex-col flex-1 min-h-0 relative" data-id="element-658">
        {messages.length === 0 ? <EmptyChatSuggestions mode={currentChatMode} onModeChange={handleModeChange} onShortcutClick={handleShortcutClick} data-id="element-659" /> : <MessageList messages={messages} isTyping={isTyping} onPreviewClick={handlePreviewClick} onWidgetResponse={handleWidgetResponse} onWidgetSkip={handleWidgetSkip} onShortcutClick={handleShortcutClick} data-id="element-660" />}

        <BottomBar onSendMessage={handleSendMessage} disabled={isTyping} chatMode={currentChatMode} onUseTemplate={handleUseTemplate} data-id="element-661" />
      </div>

      <TaskModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} checklist={selectedChecklist} categoryColor={selectedChecklist?.categoryColor} onGoToTasks={() => setIsModalOpen(false)} data-id="element-662" />

      <OverwriteConfirmationModal isOpen={isOverwriteModalOpen} onClose={() => {
      setIsOverwriteModalOpen(false);
      setPendingChecklistUpdate(null);
    }} onOverwrite={handleOverwrite} onKeepCompleted={handleKeepCompleted} data-id="element-663" />

      <style data-id="element-664">{`
        @keyframes bannerSlideIn {
          from {
            opacity: 0;
            transform: translateY(-12px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>;
}